import React from "react"
import type { FC } from "react"

import { Page404 } from "@/components/nav/page404"
import { useAuthentication } from "@/contexts"

import type { RoleGuardProps } from "./role-guard.types"

/**
 * A guard that displays a 404 UI when the user is not one of the allowed roles
 *
 * @example
 * <RoleGuard allowedRoles={["consultant_user"]}>
 *
 * @example
 * <RoleGuard disallowedRoles={["consultant_user"]}>
 */
export const RoleGuard: FC<RoleGuardProps> = ({
  allowedRoles,
  children,
  disallowedRoles,
}) => {
  const { activeRole, activeUser } = useAuthentication()

  // activeRole is populated asynchronously after activeUser; avoid
  // treating this transient state as unauthorized.
  if (!activeUser || !activeRole) {
    return null
  }

  const isAllowed = allowedRoles
    ? allowedRoles.includes(activeRole)
    : !disallowedRoles?.includes(activeRole)

  return isAllowed ? <>{children}</> : <Page404 />
}
